/* eslint-disable eqeqeq */
/* eslint-disable no-console */
import React, { memo, useCallback, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { Row, Col } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import axios from "axios";
import { useDispatch } from "react-redux";
import { CustomInputWithController } from "../../Core/Components/CustomInput";
import Button from "../../Core/Components/CustomButton";
import BlockedEyeIcon from "../../Common/Svgs/BlockedEyeIcon";
import { updateLoggedInUser } from "../../Redux/Slices/loggedInUser.slice";
import ToastMessageModal from "../Login/ToastMessageModal";
import { SEED_ROLES } from "../Login/constants";
import ArrowIcon from "../../Common/Svgs/ArrowIcon";

// validations
const validationForPassword = {
  required: "please enter password",
  minLength: {
    value: 8,
    message: "less characters for password",
  },
};

const validationForEmail = {
  required: "please enter email address",
  pattern: {
    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$/,
    message: "Enter a vaild email",
  },
};

const SignUpScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();

  const { handleSubmit, control, formState, watch, setValue, setError } =
    useForm({
      defaultValues: {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
      },
    });

  const localPassword = watch("password");
  const localConfirmPassword = watch("confirmPassword");

  const [focus, setFocus] = useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false,
    confirmPassword: false,
  });
  const [showIcon, setShowIcon] = useState({
    password: false,
    confirmPassword: false,
  });

  const [tenantError, setTenantError] = useState({
    visible: false,
    description: "",
  });

  const verifyToken = useCallback(() => {
    const token = searchParams.get("token");
    const decoded = jwtDecode(token);

    const expiry = decoded.exp * 1000;
    const current = new Date();

    if (expiry < current.getTime()) {
      navigate("/invitation-expired");
    } else {
      if (decoded.email) setValue("email", decoded.email);
      if (decoded.first_name) setValue("firstName", decoded.first_name);
      if (decoded.last_name) setValue("lastName", decoded.last_name);
    }
  }, [searchParams, setValue, navigate]);

  const onSubmit = async (e, isMobile) => {
    const token = searchParams.get("token");

    const payload = {
      firstName: e.firstName.trim(),
      lastName: e.lastName.trim(),
      email: e.email.trim(),
      password: e.password.trim(),
      confirmPassword: e.confirmPassword.trim(),
      token,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/sign-up`,
        payload,
      );

      if (response.status === 200) {
        if (isMobile) {
          // before -> 12/12/2025 -> if (Object.values(SEED_ROLES).includes(response.data.roleId)) {

          // if user is signing up through mobile and is a super admin or hub manager then show continue your journey on mobile app
          if (
            response.data.roleId == SEED_ROLES.SUPER_ADMIN ||
            response.data.roleId == SEED_ROLES.HUB_MANAGER
          ) {
            navigate("/sign-up-successful?redirectCode=1");
          }
          // if user is signing up through mobile and is a driver then install or open mobile app
          else if (response.data.roleId == SEED_ROLES.DRIVER) {
            navigate("/sign-up-successful?redirectCode=3");
          }
          // no seed user, continue your journey on website
          else {
            navigate("/sign-up-successful?redirectCode=2");
          }
        } else {
          localStorage.clear();

          const { message, token: newToken, ...rest } = response.data;
          localStorage.setItem("token", newToken);

          dispatch(updateLoggedInUser({ key: "userDetails", value: rest }));
          navigate("/home");
        }
      }
    } catch (error) {
      console.log(error);

      if (error?.response?.data?.code === 1) {
        navigate("/invitation-expired");
      }

      setError(error?.response?.data?.type || "confirmPassword", {
        type: "server",
        message: error.response.data.message,
      });

      if (error?.response?.data?.message === "Access denied") {
        const description =
          error?.response?.data?.description || "The tenant has been deleted.";

        setTenantError({ visible: true, description });
      }
    }
  };

  // verifying the token
  useEffect(() => {
    if (searchParams.has("token")) verifyToken();
  }, [searchParams, verifyToken]);

  useEffect(() => {
    let timeout;
    if (tenantError) {
      timeout = setTimeout(() => {
        setTenantError(false);
      }, 5000);
    }

    return () => clearTimeout(timeout);
  }, [tenantError]);

  return (
    <>
      <div className="hidden lg:block w-screen h-screen">
        <Row className="h-full">
          <Col span={15} className="h-full overflow-hidden">
            <img
              src="/assets/images/Login/login-image-new.png"
              alt="LoginImage"
              className="object-cover w-full h-full"
            />
          </Col>

          <Col span={9}>
            <div className="flex flex-col items-center justify-center w-full h-full gap-y-6 bg-background-white">
              <img
                src="/assets/songa-main-logo.svg"
                alt="Songa Mobility"
                className="w-[258px] h-14"
              />

              <div>
                <p className="font-poppins text-base font-medium text-center capitalize">
                  Welcome!
                </p>

                <p className="mt-2 font-poppins text-xs font-light text-center text-dark-gray">
                  Please create your account.
                </p>
              </div>

              <form
                onSubmit={handleSubmit((data) => onSubmit(data, false))}
                className="flex flex-col items-center justify-center w-full gap-y-6"
              >
                <CustomInputWithController
                  intent="login"
                  control={control}
                  formState={formState}
                  focus={focus.firstName}
                  setFocus={(e) => setFocus({ ...focus, firstName: e })}
                  name="firstName"
                  placeholder="Enter Your First Name"
                  rules={{ required: "Please enter first name" }}
                />

                <CustomInputWithController
                  intent="login"
                  control={control}
                  formState={formState}
                  focus={focus.lastName}
                  setFocus={(e) => setFocus({ ...focus, lastName: e })}
                  name="lastName"
                  placeholder="Enter Your Last Name"
                  rules={{ required: "Please enter last name" }}
                />

                <CustomInputWithController
                  intent="login"
                  control={control}
                  formState={formState}
                  focus={focus.email}
                  setFocus={(e) => setFocus({ ...focus, email: e })}
                  name="email"
                  placeholder="Enter Your Email"
                  rules={validationForEmail}
                />

                <CustomInputWithController
                  inputType={showIcon.password ? "text" : "password"}
                  intent="login"
                  control={control}
                  formState={formState}
                  focus={focus.password}
                  setFocus={(e) => setFocus({ ...focus, password: e })}
                  name="password"
                  placeholder="Set Password"
                  rules={validationForPassword}
                  suffix={
                    <button
                      type="button"
                      onClick={() => {
                        if (localPassword) {
                          setShowIcon((e) => ({ ...e, password: !e.password }));
                        }
                      }}
                      disabled={!localPassword}
                    >
                      {showIcon.password ? (
                        <img src="/images/eye.png" className="w-5 h-5" alt="" />
                      ) : (
                        <BlockedEyeIcon
                          className={
                            localPassword ? "text-black" : "text-dark-gray"
                          }
                        />
                      )}
                    </button>
                  }
                />

                <CustomInputWithController
                  inputType={showIcon.confirmPassword ? "text" : "password"}
                  intent="login"
                  control={control}
                  formState={formState}
                  focus={focus.confirmPassword}
                  setFocus={(e) => setFocus({ ...focus, confirmPassword: e })}
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  rules={validationForPassword}
                  suffix={
                    <button
                      type="button"
                      onClick={() => {
                        if (localConfirmPassword) {
                          setShowIcon((e) => ({
                            ...e,
                            confirmPassword: !e.confirmPassword,
                          }));
                        }
                      }}
                      disabled={!localConfirmPassword}
                    >
                      {showIcon.confirmPassword ? (
                        <img src="/images/eye.png" className="w-5 h-5" alt="" />
                      ) : (
                        <BlockedEyeIcon
                          className={
                            localConfirmPassword
                              ? "text-black"
                              : "text-dark-gray"
                          }
                        />
                      )}
                    </button>
                  }
                />

                <Button type="submit" width="login">
                  Sign Up
                </Button>
              </form>
              <div className="flex justify-between mt-4 w-[312px]">
                <p className="font-poppins font-medium text-sm w-fit">
                  Already have an account?
                </p>

                <div
                  className="flex items-center justify-center gap-2"
                  onClick={() => navigate("/login")}
                >
                  <p className="font-poppins font-medium text-sm leading-none text-green underline cursor-pointer">
                    Login
                  </p>
                  <ArrowIcon className="rotate-90 text-green" />
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <div className="block lg:hidden w-screen h-screen bg-background-white overflow-auto">
        <div className="w-full h-[259px] relative">
          {/* <img
            src="/assets/images/Signup/signup-mobile.png"
            alt="Signup Header"
            className="w-full h-full object-cover object-[center_-60px]"
          /> */}
          <img
            src="/assets/images/Signup/signup-mobile-new.png"
            alt="Signup Header"
            className="w-full h-full object-cover object-[center_-60px]"
          />

          <div className="absolute inset-0 flex flex-col pt-8 pb-6 px-6 gap-10">
            <img
              src="/assets/images/Signup/logo.png"
              alt="Songa Mobility"
              className="w-10 h-10"
            />
            <div>
              <p className="text-white text-xl font-semibold">
                Welcome to Songa Mobility!
              </p>
              <p className="text-white/80 text-sm mt-1">
                Please enter your registered email to continue.
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 flex flex-col items-center w-full">
          <form
            onSubmit={handleSubmit((data) => onSubmit(data, true))}
            className="flex flex-col gap-y-5 w-full"
          >
            <div className="w-full">
              <p className="font-normal text-sm text-grey-200 leading-none text-left mb-[6px]">
                First name
              </p>
              <CustomInputWithController
                intent="mobile"
                text="mobile"
                placeholderText="mobile"
                control={control}
                formState={formState}
                focus={focus.firstName}
                setFocus={(e) => setFocus({ ...focus, firstName: e })}
                name="firstName"
                placeholder="Enter first name"
                rules={{ required: "Please enter first name" }}
              />
            </div>

            <div className="w-full">
              <p className="font-normal text-sm text-grey-200 leading-none text-left mb-[6px]">
                Last name
              </p>
              <CustomInputWithController
                intent="mobile"
                text="mobile"
                placeholderText="mobile"
                control={control}
                formState={formState}
                focus={focus.lastName}
                setFocus={(e) => setFocus({ ...focus, lastName: e })}
                name="lastName"
                placeholder="Enter last name"
                rules={{ required: "Please enter last name" }}
              />
            </div>

            <div className="w-full">
              <p className="font-normal text-sm text-grey-200 leading-none text-left mb-[6px]">
                Email
              </p>
              <CustomInputWithController
                intent="mobile"
                text="mobile"
                placeholderText="mobile"
                control={control}
                formState={formState}
                focus={focus.email}
                setFocus={(e) => setFocus({ ...focus, email: e })}
                name="email"
                placeholder="Enter email address"
                rules={validationForEmail}
              />
            </div>

            <div className="w-full">
              <p className="font-normal text-sm text-grey-200 leading-none text-left mb-[6px]">
                Password
              </p>
              <CustomInputWithController
                inputType={showIcon.password ? "text" : "password"}
                intent="mobile"
                text="mobile"
                placeholderText="mobile"
                control={control}
                formState={formState}
                focus={focus.password}
                setFocus={(e) => setFocus({ ...focus, password: e })}
                name="password"
                placeholder="Enter password"
                rules={validationForPassword}
                suffix={
                  <button
                    type="button"
                    onClick={() => {
                      if (localPassword) {
                        setShowIcon((e) => ({ ...e, password: !e.password }));
                      }
                    }}
                    disabled={!localPassword}
                  >
                    {showIcon.password ? (
                      <img src="/images/eye.png" className="w-5 h-5" alt="" />
                    ) : (
                      <BlockedEyeIcon
                        className={
                          localPassword ? "text-black" : "text-dark-gray"
                        }
                      />
                    )}
                  </button>
                }
              />
            </div>

            <div className="w-full">
              <p className="font-normal text-sm text-grey-200 leading-none text-left mb-[6px]">
                Confirm password
              </p>
              <CustomInputWithController
                inputType={showIcon.confirmPassword ? "text" : "password"}
                intent="mobile"
                text="mobile"
                placeholderText="mobile"
                control={control}
                formState={formState}
                focus={focus.confirmPassword}
                setFocus={(e) => setFocus({ ...focus, confirmPassword: e })}
                name="confirmPassword"
                placeholder="Confirm password"
                rules={validationForPassword}
                suffix={
                  <button
                    type="button"
                    onClick={() => {
                      if (localConfirmPassword) {
                        setShowIcon((e) => ({
                          ...e,
                          confirmPassword: !e.confirmPassword,
                        }));
                      }
                    }}
                    disabled={!localConfirmPassword}
                  >
                    {showIcon.confirmPassword ? (
                      <img src="/images/eye.png" className="w-5 h-5" alt="" />
                    ) : (
                      <BlockedEyeIcon
                        className={
                          localConfirmPassword ? "text-black" : "text-dark-gray"
                        }
                      />
                    )}
                  </button>
                }
              />
            </div>

            <Button type="submit" width="full">
              Sign Up
            </Button>
          </form>

          <div className="flex justify-between w-full mt-4">
            <p className="font-poppins font-medium text-sm w-fit">
              Already have an account?
            </p>

            <div
              className="flex items-center justify-center gap-2"
              onClick={() => {
                const deeplink = "myapp://login";
                window.location.href = deeplink;
              }}
            >
              <p className="font-poppins font-medium text-sm leading-none text-green underline">
                Login
              </p>
              <ArrowIcon className="rotate-90 text-green" />
            </div>
          </div>
        </div>
      </div>

      {tenantError?.visible ? (
        <ToastMessageModal
          visible={tenantError?.visible}
          onCancel={() => setTenantError({ visible: false, description: "" })}
          title="Access Denied"
          description={tenantError?.description}
        />
      ) : null}
    </>
  );
};
export default memo(SignUpScreen);
